// src/pages/builder/VoiceSettingsPanel.js
//
// 🔊 READ-ALOUD VOICE — Layout Builder panel. One place for how every
// "read it to me" in the app sounds.
//
//   Voice     which natural voice (Google Gemini text-to-speech voices)
//   Tone      how it reads: warm teacher, calm, upbeat, steady, or your own words
//   Speed     questions, answers, and everything else — each on its own
//
// Saved to rewardSettings/main.voice = { name, style, speed: { question,
// answer, other } }.
//   * Voice + tone are used by the speakText Cloud Function (functions/voice.js)
//     when it makes a sentence. Changing either means each sentence is made
//     again the first time it's heard — "Get every game's voice ready" below
//     does that ahead of time so nobody waits.
//   * Speeds are applied while playing (services/cloudVoice.js), so changing
//     them is instant and free.
//
// ▶ Try it plays a sample with the settings on screen, before saving
// (admins only — the function only accepts a trial voice from an admin).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebaseConfig";
import { saveRewardSettings, useRewardSettings } from "../../services/rewardSettingsService";
import {
  DEFAULT_SPEEDS,
  SPEED_MAX,
  SPEED_MIN,
  prepareSpeech,
  speechCacheKey,
} from "../../services/cloudVoice";
import { LETTERS } from "../../components/quizzes/kit/QuizKit";
import "./voiceSettings.css";

// Gemini's prebuilt voices. "Aoede" is the one the app has used so far.
const VOICES = [
  ["Aoede", "Breezy, warm (current default)"],
  ["Achird", "Friendly"],
  ["Sulafat", "Warm"],
  ["Leda", "Youthful"],
  ["Kore", "Firm, clear"],
  ["Zephyr", "Bright"],
  ["Autonoe", "Bright"],
  ["Puck", "Upbeat"],
  ["Laomedeia", "Upbeat"],
  ["Sadachbia", "Lively"],
  ["Fenrir", "Excitable"],
  ["Vindemiatrix", "Gentle"],
  ["Achernar", "Soft"],
  ["Despina", "Smooth"],
  ["Algieba", "Smooth"],
  ["Callirrhoe", "Easy-going"],
  ["Umbriel", "Easy-going"],
  ["Zubenelgenubi", "Casual"],
  ["Iapetus", "Clear"],
  ["Erinome", "Clear"],
  ["Schedar", "Even"],
  ["Charon", "Informative"],
  ["Rasalgethi", "Informative"],
  ["Sadaltager", "Knowledgeable"],
  ["Orus", "Firm"],
  ["Alnilam", "Firm"],
  ["Pulcherrima", "Forward"],
  ["Gacrux", "Mature"],
  ["Enceladus", "Breathy"],
  ["Algenib", "Gravelly"],
];
const DEFAULT_VOICE = "Aoede";

// "" = the function's own default wording (functions/voice.js DEFAULT_STYLE),
// so choosing "Warm teacher" keeps every sentence already made.
const A_RULE = ' Say the letter A as "ay"';
const TONES = [
  { key: "warm", label: "Warm teacher (current)", style: "" },
  { key: "gentle", label: "Calm and gentle", style: `Say this in a calm, gentle, soothing voice at a natural pace, like a kind teacher reading to young children.${A_RULE}` },
  { key: "upbeat", label: "Upbeat and excited", style: `Say this in a bright, upbeat, excited voice at a lively pace, like a fun game-show host for young children.${A_RULE}` },
  { key: "steady", label: "Clear and steady", style: `Say this clearly and evenly at a steady pace, like a teacher reading a test question aloud.${A_RULE}` },
  { key: "custom", label: "My own words…", style: null },
];
const toneKeyOf = (style) => TONES.find((t) => t.style !== null && t.style === (style || ""))?.key || "custom";

const SPEED_ROWS = [
  ["question", "Questions", "Questions and instructions in the quiz games"],
  ["answer", "Answers", "Answer choices and cards in the quiz games"],
  ["other", "Everything else", "Bonus rounds, other games and the rest of the app"],
];

const SAMPLE = {
  question: "Which force pulls things down toward the ground?",
  answers: [
    ["A.", "Gravity"],
    ["B.", "Magnetism"],
    ["C.", "Friction"],
  ],
  other: "Great job! You opened the treasure chest. Keep going to find the next one!",
};

// Same strings the games read (MultipleChoice / DragDropQuiz), so what is
// made here is exactly what a student's 🔊 asks for.
const ddCard = (o) => (o?.text && String(o.text).trim()) || (o?.image ? "[picture]" : "");
const ddBox = (o) => (o?.targetText && String(o.targetText).trim()) || (o?.text && String(o.text).trim()) || (o?.targetImage ? "[picture]" : "");
const sentencesOf = (quiz) => {
  const out = [];
  (Array.isArray(quiz?.questions) ? quiz.questions : []).forEach((q) => {
    if (Array.isArray(q?.answers) && q.answers.length) {
      out.push(q.question);
      q.answers.forEach((a) => a?.text && out.push(a.text));
    } else if (Array.isArray(q?.options) && q.options.length) {
      out.push(q.question || "Match each card to its box.");
      q.options.forEach((o) => {
        out.push(ddBox(o));
        out.push(ddCard(o));
      });
    }
  });
  return out;
};

const fmt = (n) => `${Number(n).toFixed(2).replace(/0$/, "")}×`;

const VoiceSettingsPanel = () => {
  const { settings, loaded } = useRewardSettings();
  const saved = useMemo(() => settings?.voice || {}, [settings]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(DEFAULT_VOICE);
  const [tone, setTone] = useState("warm");
  const [custom, setCustom] = useState("");
  const [speed, setSpeed] = useState(DEFAULT_SPEEDS);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { tone, text }
  const [trying, setTrying] = useState(""); // "" | "making" | "playing"
  const audioRef = useRef(null);
  const tryRun = useRef(0);
  const [prep, setPrep] = useState(null); // { done, total, made, failed, running }
  const prepStop = useRef(false);

  // load the saved settings (until the admin starts editing)
  useEffect(() => {
    if (!loaded || touched) return;
    setName(saved.name || DEFAULT_VOICE);
    const k = toneKeyOf(saved.style);
    setTone(k);
    setCustom(k === "custom" ? saved.style || "" : "");
    setSpeed({ ...DEFAULT_SPEEDS, ...(saved.speed || {}) });
  }, [loaded, saved, touched]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const style = tone === "custom" ? custom.trim().slice(0, 200) : TONES.find((t) => t.key === tone)?.style || "";
  const voiceChanged = (name || DEFAULT_VOICE) !== (saved.name || DEFAULT_VOICE) || (style || "") !== (saved.style || "");
  const edit = (fn) => (...a) => {
    setTouched(true);
    setMsg(null);
    fn(...a);
  };

  /* ---------- ▶ Try it ---------- */

  const stopTry = () => {
    tryRun.current += 1;
    audioRef.current?.pause();
    setTrying("");
  };

  const tryIt = async (which) => {
    stopTry();
    const run = tryRun.current;
    const list =
      which === "other"
        ? [{ text: SAMPLE.other, kind: "other" }]
        : [{ text: SAMPLE.question, kind: "question" }, ...SAMPLE.answers.flat().map((t) => ({ text: t, kind: "answer" }))];
    setTrying("making");
    setMsg(null);
    try {
      const call = httpsCallable(functions, "speakText", { timeout: 60000 });
      const urls = await Promise.all(
        list.map((p) => call({ text: speechCacheKey(p.text), voice: name, style }).then((r) => r?.data?.url))
      );
      for (let i = 0; i < list.length; i += 1) {
        if (run !== tryRun.current) return;
        setTrying("playing");
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          const a = new Audio(urls[i]);
          audioRef.current = a;
          a.playbackRate = Number(speed[list[i].kind]) || 1;
          try {
            a.preservesPitch = true;
          } catch {
            /* older browsers */
          }
          a.onended = resolve;
          a.onerror = resolve;
          a.play().catch(resolve);
        });
      }
    } catch (e) {
      const code = String(e?.code || "");
      setMsg({
        tone: "error",
        text: code.includes("not-found")
          ? "The voice function (speakText) isn’t deployed yet: firebase deploy --only functions:speakText"
          : `Couldn’t make the sample: ${e?.message || e}`,
      });
    }
    if (run === tryRun.current) setTrying("");
  };

  /* ---------- Save ---------- */

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const clean = {};
      SPEED_ROWS.forEach(([k]) => {
        const n = Number(speed[k]);
        clean[k] = Number.isFinite(n) ? Math.max(SPEED_MIN, Math.min(SPEED_MAX, Math.round(n * 100) / 100)) : DEFAULT_SPEEDS[k];
      });
      // "Aoede" and "" both mean the default voice; store "" for the default
      // tone so sentences already made are kept.
      await saveRewardSettings({ voice: { name: name === DEFAULT_VOICE ? "" : name, style, speed: clean } });
      setTouched(false);
      setMsg({
        tone: "ok",
        text: voiceChanged
          ? "Saved. New voice/tone: each sentence is made again the first time it’s heard. In about a minute, press “Get every game’s voice ready” so nobody waits."
          : "Saved. The new speeds apply straight away, everywhere.",
      });
    } catch (e) {
      setMsg({ tone: "error", text: `Couldn’t save: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Get every game's voice ready ---------- */

  const prepareAll = async () => {
    setMsg(null);
    prepStop.current = false;
    setPrep({ done: 0, total: 0, failed: 0, running: true, stage: "Finding every game’s sentences…" });
    let texts;
    try {
      const snap = await getDocs(collection(db, "quizzes"));
      const all = new Set(LETTERS.map((l) => speechCacheKey(`${l}.`)));
      snap.forEach((d) => sentencesOf(d.data()).forEach((t) => t && /[a-z0-9]/i.test(t) && all.add(speechCacheKey(t))));
      texts = [...all];
    } catch (e) {
      setPrep(null);
      setMsg({ tone: "error", text: `Couldn’t read the games: ${e?.message || e}` });
      return;
    }
    const state = { done: 0, total: texts.length, failed: 0, running: true, stage: "" };
    setPrep({ ...state });
    let next = 0;
    const worker = async () => {
      while (!prepStop.current && next < texts.length) {
        const t = texts[next];
        next += 1;
        // eslint-disable-next-line no-await-in-loop
        const url = await prepareSpeech(t);
        if (!url) state.failed += 1;
        state.done += 1;
        setPrep({ ...state });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setPrep({ ...state, running: false, stopped: prepStop.current });
  };

  return (
    <section className="lb-card vs-panel">
      <div className="lb-card-head">
        <span className="lb-step">🔊</span>
        <div>
          <h2>Read-aloud voice</h2>
          <p className="lb-hint">
            The voice, tone and speed of every “read it to me” in the app — questions, answers, games and the rest.
          </p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div className="vs-body">
          <div className="vs-row">
            <label className="vs-field">
              <span className="vs-label">Voice</span>
              <select value={name} onChange={edit((e) => setName(e.target.value))}>
                {VOICES.map(([v, d]) => (
                  <option key={v} value={v}>
                    {v} — {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="vs-field">
              <span className="vs-label">Tone</span>
              <select value={tone} onChange={edit((e) => setTone(e.target.value))}>
                {TONES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {tone === "custom" && (
            <label className="vs-field vs-wide">
              <span className="vs-label">How should it read? ({200 - custom.length} letters left)</span>
              <textarea
                rows={2}
                maxLength={200}
                value={custom}
                placeholder='e.g. Say this slowly and kindly, like a grandparent reading a bedtime story. Say the letter A as "ay"'
                onChange={edit((e) => setCustom(e.target.value))}
              />
            </label>
          )}

          <div className="vs-speeds">
            {SPEED_ROWS.map(([k, label, hint]) => (
              <label key={k} className="vs-speed">
                <span className="vs-label">
                  {label} <b>{fmt(speed[k])}</b>
                </span>
                <input
                  type="range"
                  min={SPEED_MIN}
                  max={SPEED_MAX}
                  step={0.05}
                  value={speed[k]}
                  onChange={edit((e) => setSpeed((s) => ({ ...s, [k]: Number(e.target.value) })))}
                />
                <span className="vs-hint">
                  {hint}
                  {Number(speed[k]) !== DEFAULT_SPEEDS[k] && (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="vs-link"
                        onClick={edit(() => setSpeed((s) => ({ ...s, [k]: DEFAULT_SPEEDS[k] })))}
                      >
                        back to {fmt(DEFAULT_SPEEDS[k])}
                      </button>
                    </>
                  )}
                </span>
              </label>
            ))}
          </div>

          <div className="vs-actions">
            {trying ? (
              <button type="button" className="lb-btn lb-btn-soft" onClick={stopTry}>
                ■ {trying === "making" ? "Making the sample…" : "Stop"}
              </button>
            ) : (
              <>
                <button type="button" className="lb-btn lb-btn-soft" onClick={() => tryIt("qa")}>
                  ▶ Try a question and answers
                </button>
                <button type="button" className="lb-btn lb-btn-soft" onClick={() => tryIt("other")}>
                  ▶ Try “everything else”
                </button>
              </>
            )}
            <button type="button" className="lb-btn" onClick={save} disabled={saving || !touched}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>

          {voiceChanged && touched && (
            <div className="vs-note is-warn">
              A new voice or tone is used for every sentence from now on, so each one is made again the first time it’s
              heard. After saving, press <b>Get every game’s voice ready</b> so students don’t wait.
            </div>
          )}
          {msg && <div className={`vs-note is-${msg.tone}`}>{msg.text}</div>}

          <div className="vs-prep">
            <div>
              <b>Get every game’s voice ready</b>
              <p className="vs-hint">
                Makes the natural voice for every question and answer in every published Multiple Choice and Drag and
                Drop game now, so the first 🔊 is instant for everyone. Sentences already made are skipped (free). New
                ones cost a fraction of a cent each. Keep this tab open while it runs.
              </p>
            </div>
            {prep?.running ? (
              <button type="button" className="lb-btn lb-btn-soft" onClick={() => (prepStop.current = true)}>
                ■ Stop
              </button>
            ) : (
              <button type="button" className="lb-btn" onClick={prepareAll}>
                🔊 Get every game’s voice ready
              </button>
            )}
          </div>
          {prep && (
            <div className="vs-progress">
              <div className="vs-progress-bar" style={{ width: `${prep.total ? Math.round((prep.done / prep.total) * 100) : 0}%` }} />
              <span>
                {prep.stage ||
                  (prep.running
                    ? `${prep.done} of ${prep.total} sentences ready…`
                    : `${prep.stopped ? "Stopped" : "Done"}: ${prep.done - prep.failed} of ${prep.total} ready${
                        prep.failed ? ` · ${prep.failed} couldn’t be made (press again to retry)` : ""
                      }`)}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default VoiceSettingsPanel;
